/*eslint-disable*/
import React, { useEffect, useRef, useState } from 'react';
// react components for routing our app without refresh
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// @mui/material components
import { makeStyles } from '@mui/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';
import { Autocomplete, TextField, useMediaQuery } from '@mui/material';
import Icon from '@mui/material/Icon';
import d2rshopIcon from '../../assets/img/d2rshopicon.png';

// @mui/icons-material
import {
  Search,
  InfoOutlined,
  MenuBookOutlined,
  SchoolOutlined,
  NumbersOutlined,
  FestivalOutlined,
  RuleOutlined,
  AdminPanelSettingsOutlined,
  BookmarkBorderOutlined,
  YoutubeSearchedForOutlined,
  ConstructionOutlined,
  LogoutOutlined,
  PersonOutline,
  SettingsOutlined,
  Diversity1Outlined,
  HandshakeOutlined,
  WallpaperOutlined,
  QueryStatsOutlined,
  LockClockOutlined,
  LoginOutlined,
  PersonAddOutlined,
  GavelOutlined,
  SupportAgent as SupportAgentIcon,
  ShoppingCartCheckoutOutlined,
  AddShoppingCartOutlined,
  Computer,
  AnalyticsOutlined,
  PriceCheckOutlined,
  InventoryOutlined,
  SwapHorizOutlined,
  ListAltOutlined,
  LocalShippingOutlined,
  FiberNewOutlined,
  Circle,
  StarBorderOutlined,
  VisibilityOffOutlined,
  FilterAltOutlined,
  ViewModuleOutlined,
  AutoAwesomeOutlined,
  ShieldOutlined,
  BlurOnOutlined,
  HelpOutlineOutlined,
  SportsKabaddiOutlined,
  SecurityOutlined,
  AutoFixHighOutlined,
  DiamondOutlined,
  TagOutlined,
  PriceChangeOutlined,
  TrendingUpOutlined,
  SportsEsportsOutlined,
  Inventory2Outlined,
  CheckCircleOutline,
} from '@mui/icons-material';

// Character class imports
import barbarianClass from '../../assets/img/barb.png';
import sorceressClass from '../../assets/img/soso.png';
import amazonClass from '../../assets/img/ama.png';
import assassinClass from '../../assets/img/assa.png';
import necromancerClass from '../../assets/img/nec.png';
import paladinClass from '../../assets/img/pala.png';
import druidClass from '../../assets/img/dudu.png';

// core components
import CustomDropdown from 'components/CustomDropdown/CustomDropdown.js';

import styles from 'assets/jss/material-kit-react/components/headerLinksStyle.js';
import config from '../../config/config.json';
import filterPresets from '../../config/filterPresets.json';
import axios from 'axios';
import ladderOn from 'assets/img/ladder_on.png';
import ladderOff from 'assets/img/ladder_off.png';
import hardcoreOn from 'assets/img/hardcore_on.png';
import hardcoreOff from 'assets/img/hardcore_off.png';
import chestImg from 'assets/img/d2rchest.png';
import { useTranslation } from 'react-i18next';
import { useHeaderLinks } from 'components/Header/HeaderLinksContext';
import cubeImg from '../../assets/img/horadric_cube.png';
import d2rsIcon from '../../assets/img/d2rsiconblock.png';
import { useUserData } from '../../services/UserService';
import { secondaryColor } from '../../assets/jss/material-kit-react';
import { useTheme } from '@mui/material/styles';
import d2ricon from 'assets/img/d2r-logo.png';
import d2rsicon from 'assets/img/d2rsiconblock.png';
import { useQuery } from '@tanstack/react-query';
import { D2RSService } from '../../services/d2rsService';
import { useStaticData } from '../../hooks/useStaticData';
import {
  clearStatFilters,
  setRarity,
  setType,
  setItemType,
  setNewItems,
  setEthereal,
  setUnidentified,
  setLimitFilter,
  setViewType,
  setStatFilterGroups,
  applyFilterSearch,
  setSearchTerm,
} from '../../features/filters/filterSlice';

// Add import for raritiesCategories
import raritiesCategories from '../../config/raritiesCategories.json';

const useStyles = makeStyles(styles, { name: 'CustomHeaderLinks' });

export default function HeaderLinks(props) {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Add useStaticData hook to access types, categories, and stats
  const { typesQuery, categoriesQuery, raritiesQuery, statsQuery } = useStaticData();

  const { data: userData, isLoading, isError } = useUserData();
  const isMobile = useMediaQuery('(max-width:900px)');
  const {
    disablePlatform,
    currentContext,
    setCurrentContext,
    availableNodes,
    currentNode,
    isLoggedIn,
    isNodeOwner,
    isStaff,
    isD2RS,
    userPermissions,
    updateSessionToken,
  } = useHeaderLinks();

  // Helper function to apply filters with search term generation
  const applyFiltersWithSearchTerm = (filters) => {
    const filterState = {
      rarity: filters.rarity || '',
      type: filters.type || '',
      itemType: filters.itemType || '',
      newItems: filters.newItems || '',
      ethereal: filters.ethereal || '',
      unidentified: filters.unidentified || '',
      limitFilter: filters.limitFilter || 100,
      viewType: filters.viewType || 'all',
      priceFilter: 'all',
      excludeStatFilters: filters.excludeStatFilters || [],
      statFilterGroups: filters.statFilterGroups || [],
      isFilterBubbleOpen: false,
      savedSearches: [],
      savedSearchSent: false,
      searchTerm: '',
      shouldApplySearch: false,
    };

    const searchTerm = D2RSService.convertFiltersToSearchString(filterState);
    if (searchTerm) {
      dispatch(setSearchTerm(searchTerm));
    }

    dispatch(applyFilterSearch());
    D2RSService.navigateWithFilters(filterState, navigate, 'Shop', dispatch);
  };

  const applyFiltersDirectly = (filters) => {
    // Convert old format filters to new format if needed
    const migrateOldFilters = (oldFilters) => {
      if ('addRarityFilterID' in oldFilters) {
        const rarityMap = {
          0: '',
          1: 'unique',
          2: 'set',
          3: 'rare',
          4: 'magic',
          5: 'white',
          6: 'runeword',
          7: 'misc',
          8: 'gem',
          unique: 'unique', // Handle already converted values
        };

        return {
          rarity: rarityMap[oldFilters.addRarityFilterID] || '',
          type: oldFilters.addTypeFilterID === '0' ? '' : oldFilters.addTypeFilterID || '',
          itemType:
            oldFilters.addItemTypeFilterID === '0' ? '' : oldFilters.addItemTypeFilterID || '',
          newItems: '',
          ethereal: '',
          unidentified: '',
          limitFilter: 100,
          viewType: 'all',
          priceFilter: 'all',
          statFilters: [],
          statFilterGroups: [],
          excludeStatFilters: [],
        };
      }
      return oldFilters;
    };

    const migratedFilters = migrateOldFilters(filters);

    // Clear ALL existing filters first
    dispatch(clearStatFilters());
    dispatch(setStatFilterGroups([]));
    dispatch(setRarity(''));
    dispatch(setType(''));
    dispatch(setItemType(''));
    dispatch(setNewItems(''));
    dispatch(setEthereal(''));
    dispatch(setUnidentified(''));
    dispatch(setLimitFilter(100));
    dispatch(setViewType('all'));

    // Apply new filters
    if (migratedFilters.rarity && migratedFilters.rarity !== '') {
      dispatch(setRarity(migratedFilters.rarity));
    }
    if (migratedFilters.type && migratedFilters.type !== '') {
      dispatch(setType(migratedFilters.type));
    }
    if (migratedFilters.itemType && migratedFilters.itemType !== '') {
      dispatch(setItemType(migratedFilters.itemType));
    }
    if (migratedFilters.newItems && migratedFilters.newItems !== '') {
      dispatch(setNewItems(migratedFilters.newItems));
    }
    if (migratedFilters.ethereal && migratedFilters.ethereal !== '') {
      dispatch(setEthereal(migratedFilters.ethereal));
    }
    if (migratedFilters.unidentified && migratedFilters.unidentified !== '') {
      dispatch(setUnidentified(migratedFilters.unidentified));
    }
    if (migratedFilters.limitFilter && migratedFilters.limitFilter !== 100) {
      dispatch(setLimitFilter(migratedFilters.limitFilter));
    }
    if (migratedFilters.viewType && migratedFilters.viewType !== 'all') {
      dispatch(setViewType(migratedFilters.viewType));
    }

    // Navigate to shop
    navigate('Shop');

    // Generate search term from filters and trigger search
    const filterState = {
      rarity: migratedFilters.rarity || '',
      type: migratedFilters.type || '',
      itemType: migratedFilters.itemType || '',
      newItems: migratedFilters.newItems || '',
      ethereal: migratedFilters.ethereal || '',
      unidentified: migratedFilters.unidentified || '',
      limitFilter: migratedFilters.limitFilter || 100,
      viewType: migratedFilters.viewType || 'all',
      priceFilter: 'all',
      excludeStatFilters: [],
      statFilterGroups: migratedFilters.statFilterGroups || [],
      isFilterBubbleOpen: false,
      savedSearches: [],
      savedSearchSent: false,
      searchTerm: '',
      shouldApplySearch: false,
    };

    // Convert filters to search term using D2RSService
    const searchTerm = D2RSService.convertFiltersToSearchString(filterState);

    // Set the search term and trigger search
    if (searchTerm) {
      dispatch(setSearchTerm(searchTerm));
      dispatch(applyFilterSearch());
    }
  };

  const { data: communitiesData } = useQuery({
    queryKey: ['communities'],
    queryFn: async () => {
      try {
        const response = await fetch(`${config.backend_base_url}api/teams`, {
          headers: {
            'session-token': localStorage.getItem('session_token'),
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch communities');
        const data = await response.json();
        return data.filter((team) => team.published);
      } catch (err) {
        console.error('Error fetching communities:', err);
        return [];
      }
    },
  });

  const logoutFunc = () => {
    localStorage.clear();
    updateSessionToken(null);
    window.location.reload(true);
  };

  const [inputValue, setInputValue] = useState(currentNode || '');
  const textMeasureRef = useRef(null);
  const [inputWidth, setInputWidth] = useState('auto');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    adjustInputWidth();
  }, [inputValue]);

  const adjustInputWidth = () => {
    if (textMeasureRef.current) {
      setInputWidth(`${calculateInputWidth(textMeasureRef.current.offsetWidth) + 2}px`);
    }
  };

  const calculateInputWidth = (width) => {
    if (width <= 150) return width;

    const decrement = Math.floor((width - 150) / 50) * 8;
    return width - decrement;
  };

  const handleInputChange = (_, newValue) => {
    setInputValue(newValue);
  };

  const handleChange = (event, newValue) => {
    currentNodeFunc(event, newValue);
    setInputValue(newValue);
    setIsHovered(false);
  };

  const handleClose = () => {
    setIsHovered(false);
  };

  const handleOpen = () => {
    setIsHovered(true);
    setInputValue('');
  };

  let currentContextFunc = function (event, context) {
    event.preventDefault();
    setCurrentContext(context);
    if (context === 'Softcore Ladder') {
      sendContextChange(0, 1);
    }
    if (context === 'Softcore Non-Ladder') {
      sendContextChange(0, 0);
    }
    if (context === 'Hardcore Ladder') {
      sendContextChange(1, 1);
    }
    if (context === 'Hardcore Non-Ladder') {
      sendContextChange(1, 0);
    }
  };

  let sendContextChange = function (hardcoreValue, ladderValue) {
    const sessionToken = localStorage.getItem('session_token');
    axios
      .post(
        config.backend_base_url + 'user/update/contexts',
        {
          hardcore: hardcoreValue,
          ladder: ladderValue,
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((response) => {
        console.log('Success ========>', response.data);
        localStorage.setItem('hardcore', hardcoreValue);
        localStorage.setItem('ladder', ladderValue);
        window.location.reload(true);
      })
      .catch((error) => {
        console.log('Error ========>', error);
        window.location.reload(true);
      });
  };

  let currentNodeFunc = function (event, newnodename) {
    event.preventDefault();
    sendNodeChange(newnodename);
  };

  let sendNodeChange = function (newnodename) {
    const sessionToken = localStorage.getItem('session_token');
    const nodeName = newnodename.split('(')[0].trim();
    axios
      .post(
        config.backend_base_url + 'user/update/node',
        {
          current_node: nodeName,
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((response) => {
        console.log('Success ========>', response.data);
        localStorage.setItem('nodename', nodeName);
        window.location.reload(true);
      })
      .catch((error) => {
        console.log('Error ========>', error);
        window.location.reload(true);
      });
  };

  const replaceNodeStatusWithIcon = (text, asJSX = true, removeStatusText = false) => {
    if (!text) return '';

    if (!asJSX) {
      return text
        .replace('CAN_DROP', '')
        .replace('STATUS_ONLINE', '')
        .replace('STATUS_OFFLINE', '')
        .replace('NODE_HOST', '')
        .replace('CAN_MULE', '')
        .replace('LADDER_ENDED', '')
        .replace('IS_PUBLIC', '')
        .replace('IS_PRIVATE', '');
    }

    const parts = text.split(
      /(CAN_DROP|STATUS_ONLINE|STATUS_OFFLINE|NODE_HOST|CAN_MULE|LADDER_ENDED|IS_PUBLIC|IS_PRIVATE)/
    );
    if (parts.length === 1) return text;

    return (
      <span style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
        {parts.map((part, index) => {
          switch (part) {
            case 'CAN_DROP':
              return (
                <ShoppingCartCheckoutOutlined
                  key={index}
                  sx={{ fontSize: 16, color: secondaryColor }}
                />
              );
            case 'STATUS_ONLINE':
              return <Circle key={index} sx={{ fontSize: 16, color: '#4CAF50' }} />;
            case 'STATUS_OFFLINE':
              return <Circle key={index} sx={{ fontSize: 16, color: '#f44336' }} />;
            case 'NODE_HOST':
              return (
                <StarBorderOutlined key={index} sx={{ fontSize: 16, color: secondaryColor }} />
              );
            case 'CAN_MULE':
              return (
                <img
                  key={index}
                  style={{
                    width: '16px',
                    verticalAlign: 'middle',
                    paddingTop: '4px',
                  }}
                  src={chestImg}
                  alt="chest"
                />
              );
            case 'LADDER_ENDED':
              return (
                <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    style={{
                      width: '20px',
                      verticalAlign: 'middle',
                    }}
                    src={ladderOn}
                    alt="ladder ended"
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '0',
                      right: '0',
                      borderTop: '3px solid #f44336',
                      transform: 'translateY(-65%) rotate(-45deg)',
                      width: '120%',
                      marginLeft: '-10%',
                    }}
                  />
                </div>
              );
            case 'IS_PUBLIC':
              return (
                <Diversity1Outlined key={index} sx={{ fontSize: 16, color: secondaryColor }} />
              );
            case 'IS_PRIVATE':
              return (
                <VisibilityOffOutlined key={index} sx={{ fontSize: 16, color: secondaryColor }} />
              );
            default:
              return removeStatusText
                ? part.includes('STATUS_ONLINE') || part.includes('STATUS_OFFLINE')
                  ? ''
                  : part
                : part;
          }
        })}
      </span>
    );
  };

  const renderNodeSelector = () => {
    if (!userData?.user?.is_mehlbot || isLoading || isError) return null;

    // Group nodes by type
    const offlineNodes = availableNodes.filter((node) => node.toLowerCase().includes('offline'));

    const ladderEndedNodes = availableNodes.filter(
      (node) => !offlineNodes.includes(node) && node.includes('LADDER_ENDED')
    );

    const shopNodes = availableNodes.filter(
      (node) =>
        !offlineNodes.includes(node) &&
        !ladderEndedNodes.includes(node) &&
        (node.toLowerCase().includes('d2rshop') ||
          node.toLowerCase().includes('shoppers') ||
          node.toLowerCase().includes('d2rs'))
    );

    const yourNodes = availableNodes.filter(
      (node) =>
        !offlineNodes.includes(node) &&
        !ladderEndedNodes.includes(node) &&
        !shopNodes.includes(node) &&
        node.includes('NODE_HOST')
    );

    const publicNodes = availableNodes.filter(
      (node) =>
        !offlineNodes.includes(node) &&
        !ladderEndedNodes.includes(node) &&
        !shopNodes.includes(node) &&
        !yourNodes.includes(node) &&
        node.includes('IS_PUBLIC')
    );

    const privateNodes = availableNodes.filter(
      (node) =>
        !offlineNodes.includes(node) &&
        !ladderEndedNodes.includes(node) &&
        !shopNodes.includes(node) &&
        !yourNodes.includes(node) &&
        !publicNodes.includes(node) &&
        node.includes('IS_PRIVATE')
    );

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {replaceNodeStatusWithIcon(currentNode || t('game.selectNode'), true, true)}
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '16px',
                width: '100%',
                ...(isMobile ? { maxWidth: '224px' } : { minWidth: '800px', maxWidth: '1200px' }),
                overflowX: 'auto',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              {/* All Nodes Option */}
              <Button
                className={classes.dropdownLinkSpecial}
                onClick={(event) => currentNodeFunc(event, 'all')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  transition: 'all 0.2s ease-in-out',
                  border: `2px solid ${secondaryColor}40 !important`,
                  borderRadius: '8px',
                  width: '100%',
                  margin: '0',
                  textTransform: 'none',
                  '&:hover': {
                    border: `3px solid ${secondaryColor} !important`,
                  },
                }}
              >
                <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 600 }}>{replaceNodeStatusWithIcon('All Nodes')}</div>
                  <div style={getDescStyle()}>{t('game.allNodesDesc')}</div>
                </div>
              </Button>

              {/* Shop Nodes */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <ShoppingCartCheckoutOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('game.shopNodes')}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {shopNodes.map((node) => {
                    const nodeWithoutStatus = node
                      .replace('STATUS_ONLINE', '')
                      .replace('STATUS_OFFLINE', '');
                    const isD2RShop = node.toLowerCase().includes('d2rshop');
                    return (
                      <Link
                        key={node}
                        className={classes.dropdownLinkSpecial}
                        onClick={(event) => currentNodeFunc(event, node)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#4CAF50',
                            marginRight: '8px',
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, color: secondaryColor }}>
                            {replaceNodeStatusWithIcon(nodeWithoutStatus, true, true)}
                          </div>
                        </div>
                        <img
                          src={isD2RShop ? d2rshopIcon : d2rsicon}
                          alt={isD2RShop ? 'd2rshop' : 'd2rs'}
                          style={{
                            width: '24px',
                            marginLeft: 'auto',
                            opacity: 0.7,
                          }}
                        />
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Your Nodes */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <PersonOutline sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('game.yourNodes')}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {yourNodes.map((node) => {
                    const nodeWithoutStatus = node
                      .replace('STATUS_ONLINE', '')
                      .replace('STATUS_OFFLINE', '');
                    return (
                      <Link
                        key={node}
                        className={classes.dropdownLinkSpecial}
                        onClick={(event) => currentNodeFunc(event, node)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#4CAF50',
                            marginRight: '8px',
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, color: secondaryColor }}>
                            {replaceNodeStatusWithIcon(nodeWithoutStatus, true, true)}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Private Nodes */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <VisibilityOffOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('game.privateNodes')}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {privateNodes.map((node) => {
                    const nodeWithoutStatus = node
                      .replace('STATUS_ONLINE', '')
                      .replace('STATUS_OFFLINE', '');
                    return (
                      <Link
                        key={node}
                        className={classes.dropdownLinkSpecial}
                        onClick={(event) => currentNodeFunc(event, node)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#4CAF50',
                            marginRight: '8px',
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, color: secondaryColor }}>
                            {replaceNodeStatusWithIcon(nodeWithoutStatus, true, true)}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Public Nodes */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <Diversity1Outlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('game.publicNodes')}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px',
                  }}
                >
                  {publicNodes.map((node) => {
                    const nodeWithoutStatus = node
                      .replace('STATUS_ONLINE', '')
                      .replace('STATUS_OFFLINE', '');
                    return (
                      <Link
                        key={node}
                        className={classes.dropdownLinkSpecial}
                        onClick={(event) => currentNodeFunc(event, node)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#4CAF50',
                            marginRight: '8px',
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, color: secondaryColor }}>
                            {replaceNodeStatusWithIcon(nodeWithoutStatus, true, true)}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Ladder Ended Nodes */}
              {ladderEndedNodes.length > 0 && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      padding: '0 16px 8px 16px',
                      borderBottom: `1px solid ${secondaryColor}20`,
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        style={{
                          width: '18px',
                          verticalAlign: 'middle',
                        }}
                        src={ladderOn}
                        alt="ladder ended"
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '0',
                          right: '0',
                          borderTop: '2px solid #f44336',
                          transform: 'rotate(-45deg)',
                        }}
                      />
                    </div>
                    {t('game.ladderEndedNodes')}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '8px',
                    }}
                  >
                    {ladderEndedNodes.map((node) => {
                      const nodeWithoutStatus = node
                        .replace('STATUS_ONLINE', '')
                        .replace('STATUS_OFFLINE', '');
                      return (
                        <Link
                          key={node}
                          className={classes.dropdownLinkSpecial}
                          onClick={(event) => currentNodeFunc(event, node)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 223, 0, 0.1)',
                            },
                          }}
                        >
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#4CAF50',
                              marginRight: '8px',
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, color: secondaryColor }}>
                              {replaceNodeStatusWithIcon(nodeWithoutStatus, true, true)}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Offline Nodes */}
              {offlineNodes.length > 0 && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      padding: '0 16px 8px 16px',
                      borderBottom: `1px solid ${secondaryColor}20`,
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    <Computer sx={{ fontSize: 18, color: secondaryColor }} />
                    {t('game.offlineNodes')}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '8px',
                    }}
                  >
                    {offlineNodes.map((node) => {
                      const nodeWithoutStatus = node
                        .replace('STATUS_ONLINE', '')
                        .replace('STATUS_OFFLINE', '');
                      const isDisabled = node.toLowerCase().includes('disabled');
                      return (
                        <Link
                          key={node}
                          className={classes.dropdownLinkSpecial}
                          onClick={(event) => currentNodeFunc(event, node)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 223, 0, 0.1)',
                            },
                          }}
                        >
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: isDisabled ? '#000000' : '#f44336',
                              marginRight: '8px',
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, color: secondaryColor }}>
                              {replaceNodeStatusWithIcon(nodeWithoutStatus, true, true)}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderContextSelector = () => {
    if (disablePlatform) return null;

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <img
                src={d2ricon}
                alt="Diablo II: Resurrected"
                style={{ height: 42, verticalAlign: 'middle', objectFit: 'contain' }}
              />
              {isLoggedIn ? (
                <div style={{ display: 'flex', gap: '4px' }}>
                  {currentContext.toLowerCase() === 'softcore ladder' && (
                    <>
                      <img style={{ width: '24px' }} src={hardcoreOff} alt="hardcoreOff" />
                      <img style={{ width: '24px' }} src={ladderOn} alt="ladderOn" />
                    </>
                  )}
                  {currentContext.toLowerCase() === 'softcore non-ladder' && (
                    <>
                      <img style={{ width: '24px' }} src={hardcoreOff} alt="hardcoreOff" />
                      <img style={{ width: '24px' }} src={ladderOff} alt="ladderOff" />
                    </>
                  )}
                  {currentContext.toLowerCase() === 'hardcore ladder' && (
                    <>
                      <img style={{ width: '24px' }} src={hardcoreOn} alt="hardcoreOn" />
                      <img style={{ width: '24px' }} src={ladderOn} alt="ladderOn" />
                    </>
                  )}
                  {currentContext.toLowerCase() === 'hardcore non-ladder' && (
                    <>
                      <img style={{ width: '24px' }} src={hardcoreOn} alt="hardcoreOn" />
                      <img style={{ width: '24px' }} src={ladderOff} alt="ladderOff" />
                    </>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '2px',
                    position: 'relative',
                  }}
                >
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <img style={{ width: '12px' }} src={hardcoreOff} alt="hardcoreOff" />
                    <img style={{ width: '12px' }} src={ladderOn} alt="ladderOn" />
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <img style={{ width: '12px' }} src={hardcoreOff} alt="hardcoreOff" />
                    <img style={{ width: '12px' }} src={ladderOff} alt="ladderOff" />
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <img style={{ width: '12px' }} src={hardcoreOn} alt="hardcoreOn" />
                    <img style={{ width: '12px' }} src={ladderOn} alt="ladderOn" />
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <img style={{ width: '12px' }} src={hardcoreOn} alt="hardcoreOn" />
                    <img style={{ width: '12px' }} src={ladderOff} alt="ladderOff" />
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LockClockOutlined sx={{ fontSize: 16, color: secondaryColor }} />
                  </div>
                </div>
              )}
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
            title: !isLoggedIn ? t('navigation.loginToSpecifyCore') : undefined,
          }}
          dropdownList={[
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '8px',
                padding: '16px',
                width: '100%',
                ...(isMobile ? { maxWidth: '224px' } : { minWidth: '320px', maxWidth: '900px' }),
                overflowX: 'auto',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img
                    src={d2ricon}
                    alt="Diablo II: Resurrected"
                    title="Diablo II: Resurrected"
                    style={{ height: 42, verticalAlign: 'middle', objectFit: 'contain' }}
                  />
                </div>
                {!isLoggedIn ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '24px 16px',
                      textAlign: 'center',
                    }}
                  >
                    <LockClockOutlined sx={{ fontSize: 24, color: secondaryColor }} />
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '4px', marginRight: '80px' }}>
                        {t('navigation.loginRequired')}
                      </div>
                      <div style={{ fontSize: '14px', opacity: 0.7 }}>
                        {t('navigation.loginToSpecifyCore')}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Link
                      className={classes.dropdownLinkSpecial}
                      onClick={(event) => currentContextFunc(event, 'Softcore Ladder')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 223, 0, 0.1)',
                        },
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>Softcore Ladder</div>
                      </div>
                      <div style={{ marginLeft: 'auto' }}>
                        <img style={{ width: '16px' }} src={hardcoreOff} alt="hardcoreOff" />
                        <img style={{ width: '16px' }} src={ladderOn} alt="ladderOn" />
                      </div>
                    </Link>
                    <Link
                      className={classes.dropdownLinkSpecial}
                      onClick={(event) => currentContextFunc(event, 'Softcore Non-Ladder')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 223, 0, 0.1)',
                        },
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>Softcore Non-Ladder</div>
                      </div>
                      <div style={{ marginLeft: 'auto' }}>
                        <img style={{ width: '16px' }} src={hardcoreOff} alt="hardcoreOff" />
                        <img style={{ width: '16px' }} src={ladderOff} alt="ladderOff" />
                      </div>
                    </Link>
                    <Link
                      className={classes.dropdownLinkSpecial}
                      onClick={(event) => currentContextFunc(event, 'Hardcore Ladder')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        transition: 'all 0.2s ease-in-out',
                        color: 'red',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: 'red' }}>Hardcore Ladder</div>
                      </div>
                      <div style={{ marginLeft: 'auto' }}>
                        <img style={{ width: '16px' }} src={hardcoreOn} alt="hardcoreOn" />
                        <img style={{ width: '16px' }} src={ladderOn} alt="ladderOn" />
                      </div>
                    </Link>
                    <Link
                      className={classes.dropdownLinkSpecial}
                      onClick={(event) => currentContextFunc(event, 'Hardcore Non-Ladder')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        transition: 'all 0.2s ease-in-out',
                        color: 'red',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: 'red' }}>Hardcore Non-Ladder</div>
                      </div>
                      <div style={{ marginLeft: 'auto' }}>
                        <img style={{ width: '16px' }} src={hardcoreOn} alt="hardcoreOn" />
                        <img style={{ width: '16px' }} src={ladderOff} alt="ladderOff" />
                      </div>
                    </Link>
                  </>
                )}
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  // Helper function to create grid style based on device type
  const getGridStyle = (columns = '1fr auto 1fr auto 1fr') => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : columns,
    gap: isMobile ? '16px' : '8px',
    padding: isMobile ? '16px 0' : '16px',
    width: '100%',
    ...(isMobile ? { maxWidth: '224px' } : { minWidth: '300px', maxWidth: '1100px' }),
    overflowX: isMobile ? 'hidden' : 'hidden',
    border: '1px solid rgba(255, 223, 0, 0.2)',
    borderRadius: '8px',
  });

  // Helper function for dividers that should be hidden on mobile
  const getDividerStyle = () => ({
    display: isMobile ? 'none' : 'block',
    borderLeft: `1px solid ${secondaryColor}20`,
    height: '100%',
    margin: '0 8px',
  });

  // Helper function for text styling in dropdowns
  const getTextStyle = (props = {}) => ({
    fontWeight: 600,
    whiteSpace: isMobile ? 'normal' : 'nowrap',
    wordBreak: isMobile ? 'break-word' : 'normal',
    ...props,
  });

  // Helper function for description text styling
  const getDescStyle = (props = {}) => ({
    fontSize: '12px',
    opacity: 0.7,
    whiteSpace: isMobile ? 'normal' : 'nowrap',
    wordBreak: isMobile ? 'break-word' : 'normal',
    ...props,
  });

  const renderSearchDropdown = () => {
    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.search')}{' '}
              <ShoppingCartCheckoutOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />{' '}
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    whiteSpace: isMobile ? 'normal' : 'nowrap',
                  }}
                >
                  <ShoppingCartCheckoutOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.instantShop')}
                </div>
                <Link
                  to="/shop"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={getTextStyle()}>{t('navigation.instantShop')}</div>
                    <div style={getDescStyle()}>{t('navigation.instantShopDesc')}</div>
                  </div>
                </Link>
              </div>
              <div style={getDividerStyle()} />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    whiteSpace: isMobile ? 'normal' : 'nowrap',
                  }}
                >
                  <AddShoppingCartOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.productShop')}
                </div>
                <Link
                  to="/shop"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <AddShoppingCartOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={getTextStyle()}>{t('navigation.productShop')}</div>
                    <div style={getDescStyle()}>{t('navigation.productShopDesc')}</div>
                  </div>
                </Link>
              </div>
            </div>,
          ]}
          dropdownFooter={
            <div
              style={{
                padding: '12px 16px',
                marginTop: '8px',
                fontSize: '12px',
                opacity: 0.7,
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderTop: 'none',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
              }}
            >
              {t('navigation.shopFooter')}
            </div>
          }
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderNodePagesDropdown = () => {
    if (!isLoggedIn || !isNodeOwner) return null;

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={`${t('navigation.nodePages')} 👑`}
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                padding: '16px',
                width: '100%',
                minWidth: '600px',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              {/* Mule Games column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img
                    src={chestImg}
                    alt="chest"
                    style={{ width: 18, height: 18, verticalAlign: 'middle' }}
                  />
                  {t('navigation.muleGames')}
                </div>
                <Link
                  to="/users?t=games"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <Computer sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.games')}</div>
                    <div style={getDescStyle()}>{t('navigation.gamesDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
                <Link
                  to="/node?t=accounts"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <img src={chestImg} alt="chest" style={{ width: '20px' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.muleAccounts')}</div>
                    <div style={getDescStyle()}>{t('navigation.muleAccountsDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
              </div>

              {/* Users column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <PersonOutline sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.users')}
                </div>
                <Link
                  to="/users?t=createusers"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <PersonAddOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.createUsers')}</div>
                    <div style={getDescStyle()}>{t('navigation.createUsersDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
                <Link
                  to="/users?t=permissions"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <AdminPanelSettingsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.permissions')}</div>
                    <div style={getDescStyle()}>{t('navigation.permissionsDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
                <Link
                  to="/users?t=bookings"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <BookmarkBorderOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.bookings')}</div>
                    <div style={getDescStyle()}>{t('navigation.bookingsDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
                <Link
                  to="/users?t=fiatpayoutusers"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <PriceCheckOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.fiatPayoutUsers')}</div>
                    <div style={getDescStyle()}>{t('navigation.fiatPayoutUsersDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
              </div>

              {/* Settings column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <SettingsOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.settings')}
                </div>
                <Link
                  to="/node?t=settings&t2=settings"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <SettingsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.nodeSettings')}</div>
                    <div style={getDescStyle()}>{t('navigation.nodeSettingsDesc')}</div>
                  </div>
                  <StarBorderOutlined
                    sx={{ marginLeft: 'auto', color: secondaryColor, fontSize: 20 }}
                  />
                </Link>
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'visible',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
            PaperProps: {
              style: {
                maxHeight: 'none',
              },
            },
          }}
        />
      </ListItem>
    );
  };

  const renderToolsDropdown = () => {
    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <>
              {t('navigation.tools')}{' '}
              <ConstructionOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              {/* D2RS column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img src={d2rsicon} alt="D2RS" style={{ height: 32, verticalAlign: 'middle' }} />
                  D2RS
                </div>
                <Link
                  to={`/offers-page${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <GavelOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.offersPage')}</div>
                    <div style={getDescStyle()}>{t('navigation.offersPageDesc')}</div>
                  </div>
                  <img
                    src={d2rshopIcon}
                    alt="d2rshop"
                    style={{ width: '24px', marginLeft: 'auto' }}
                  />
                </Link>
                <Link
                  to={`/tracker${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <BookmarkBorderOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.itemTracker')}</div>
                    <div style={getDescStyle()}>{t('navigation.itemTrackerDesc')}</div>
                  </div>
                </Link>
                <Link
                  to={`/event-mod${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <FestivalOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.eventMod')}</div>
                    <div style={getDescStyle()}>{t('navigation.eventModDesc')}</div>
                  </div>
                </Link>
              </div>
              {/* Divider */}
              <div style={getDividerStyle()} />
              {/* D2R column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img
                    src={d2ricon}
                    alt="Diablo II: Resurrected"
                    style={{ height: 34, verticalAlign: 'middle', objectFit: 'contain' }}
                  />
                  Diablo II: Resurrected
                </div>
                <Link
                  to={`/saved-searches${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <YoutubeSearchedForOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.savedSearches')}</div>
                    <div style={getDescStyle()}>{t('navigation.savedSearchesDesc')}</div>
                  </div>
                </Link>
              </div>
              {/* Divider */}
              <div style={getDividerStyle()} />
              {/* Coming Soon column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <LockClockOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.comingSoon')}
                </div>
                <Link
                  to={`/image-generator${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <WallpaperOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.imageGenerator')}</div>
                    <div style={getDescStyle()}>{t('navigation.imageGeneratorDesc')}</div>
                  </div>
                  <LockClockOutlined sx={{ fontSize: 16, marginLeft: 'auto', opacity: 0.7 }} />
                </Link>
                <Link
                  to={`/price-index${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <QueryStatsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.priceIndex')}</div>
                    <div style={getDescStyle()}>{t('navigation.priceIndexDesc')}</div>
                  </div>
                  <LockClockOutlined sx={{ fontSize: 16, marginLeft: 'auto', opacity: 0.7 }} />
                </Link>
                <Link
                  to={`/char-builder${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <SportsKabaddiOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.charBuilder')}</div>
                    <div style={getDescStyle()}>{t('navigation.charBuilderDesc')}</div>
                  </div>
                  <LockClockOutlined sx={{ fontSize: 16, marginLeft: 'auto', opacity: 0.7 }} />
                </Link>
                <Link
                  to={`/discord-companion${!isLoggedIn ? '?t=howToUse' : ''}`}
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <Icon
                    className="fab fa-discord"
                    style={{ fontSize: 20, color: secondaryColor }}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.discordCompanion')}</div>
                    <div style={getDescStyle()}>{t('navigation.discordCompanionDesc')}</div>
                  </div>
                  <LockClockOutlined sx={{ fontSize: 16, marginLeft: 'auto', opacity: 0.7 }} />
                </Link>
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderInfoDropdown = () => {
    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <>
              {t('navigation.info')} <InfoOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              {/* D2RS column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img src={d2rsicon} alt="D2RS" style={{ height: 32, verticalAlign: 'middle' }} />
                  D2RS
                </div>
                <Link
                  to="/"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <img src={d2rsIcon} alt="landing" style={{ width: '20px' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.landingPage')}</div>
                    <div style={getDescStyle()}>{t('navigation.landingPageDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/tour-pages?t=tours"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <FestivalOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.tourPages')}</div>
                    <div style={getDescStyle()}>{t('navigation.tourPagesDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/tour-pages?t=rulesets"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <RuleOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.ruleSets')}</div>
                    <div style={getDescStyle()}>{t('navigation.ruleSetsDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/get-involved"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <HandshakeOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.workWithUs2')}</div>
                    <div style={getDescStyle()}>{t('navigation.workWithUs2Desc')}</div>
                  </div>
                </Link>
              </div>
              {/* Divider */}
              <div style={getDividerStyle()} />
              {/* D2R column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img
                    src={d2ricon}
                    alt="Diablo II: Resurrected"
                    style={{ height: 34, verticalAlign: 'middle', objectFit: 'contain' }}
                  />
                  Diablo II: Resurrected
                </div>
                {/* D2R info entries */}
                <Link
                  to="/guides?t=builds"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <MenuBookOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.builds')}</div>
                    <div style={getDescStyle()}>{t('navigation.buildsDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/guides?t=guides"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <SchoolOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.guides')}</div>
                    <div style={getDescStyle()}>{t('navigation.guidesDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/recipes"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <img src={cubeImg} alt="cube" style={{ width: '20px', color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.recipes')}</div>
                    <div style={getDescStyle()}>{t('navigation.recipesDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/breakpoints"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <NumbersOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.breakpoints')}</div>
                    <div style={getDescStyle()}>{t('navigation.breakpointsDesc')}</div>
                  </div>
                </Link>
              </div>
              {/* Divider */}
              <div style={getDividerStyle()} />
              {/* Communities column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <Diversity1Outlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.communities')}
                </div>
                <Link
                  to="/communities"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <Diversity1Outlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.communityHub')}</div>
                    <div style={getDescStyle()}>{t('navigation.communityHubDesc')}</div>
                  </div>
                </Link>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '12px 16px',
                  }}
                >
                  {communitiesData?.map((community) => (
                    <Link
                      key={community.id}
                      to={`/guild/${community.id}`}
                      className={classes.dropdownLinkSpecial}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        transition: 'all 0.2s ease-in-out',
                        borderRadius: '50%',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor:
                            community.type === 'discord'
                              ? 'rgba(114, 137, 218, 0.1)'
                              : 'rgba(255, 223, 0, 0.1)',
                        },
                      }}
                      title={`${community.name}\n${community.description}\nSince: ${new Date(community.created_at).toLocaleDateString()}`}
                    >
                      {community.type === 'discord' ? (
                        <Icon
                          className="fab fa-discord"
                          style={{ fontSize: 32, color: '#7289da' }}
                        />
                      ) : (
                        <img
                          src={
                            community.logo_url || (community.type === 'd2rs' ? d2rsicon : d2ricon)
                          }
                          alt={community.name}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'visible',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
            PaperProps: {
              style: {
                maxHeight: 'none',
              },
            },
          }}
        />
      </ListItem>
    );
  };

  const renderTourDropdown = () => {
    if (!isLoggedIn || !isStaff) return null;

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <>
              {t('navigation.tours')}{' '}
              <FestivalOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              {/* Events & Tournaments column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <FestivalOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.eventsAndTournaments')}
                </div>
                <Link
                  to="/tour?section=tour"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <FestivalOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.tour')}</div>
                    <div style={getDescStyle()}>{t('navigation.tourDesc')}</div>
                  </div>
                </Link>
              </div>
              {/* Divider */}
              <div style={getDividerStyle()} />
              {/* Rulesets column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <RuleOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.rulesets')}
                </div>
                <Link
                  to="/tour?section=rulesets"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <RuleOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.rulesets')}</div>
                    <div style={getDescStyle()}>{t('navigation.rulesetsDesc')}</div>
                  </div>
                </Link>
              </div>
              {/* Divider */}
              <div style={getDividerStyle()} />
              {/* Content column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <SchoolOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.content')}
                </div>
                <Link
                  to="/tour?section=guides"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <SchoolOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.guides')}</div>
                    <div style={getDescStyle()}>{t('navigation.guidesDesc')}</div>
                  </div>
                </Link>
                <Link
                  to="/tour?section=livestream"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <FestivalOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.livestream')}</div>
                    <div style={getDescStyle()}>{t('navigation.livestreamDesc')}</div>
                  </div>
                </Link>
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderPagesDropdown = () => {
    if (!isLoggedIn || userData?.user?.is_mehlbot !== 1) return null;

    const sellItems = [
      <Link
        to="/items"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.privateSearch')}</div>
          <div style={getDescStyle()}>{t('navigation.privateSearchDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/tradelists"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <ListAltOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.tradelist')}</div>
          <div style={getDescStyle()}>{t('navigation.tradelistDesc')}</div>
        </div>
        <FiberNewOutlined
          sx={{
            marginLeft: 'auto',
            color: '#4CAF50',
            fontSize: 20,
          }}
        />
      </Link>,
      <Link
        to="/items?t=trades"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <SwapHorizOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.trades')}</div>
          <div style={getDescStyle()}>{t('navigation.tradesDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/s10"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.season10Search')}</div>
          <div style={getDescStyle()}>{t('navigation.season10SearchDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/s9"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.season9Search')}</div>
          <div style={getDescStyle()}>{t('navigation.season9SearchDesc')}</div>
        </div>
      </Link>,
    ];

    const supplyItems = [
      <Link
        to="/supplier"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <img style={{ width: '20px' }} src={chestImg} alt="chest" />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.muleGamesSettings')}</div>
          <div style={getDescStyle()}>{t('navigation.muleGamesSettingsDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/sitems"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <InventoryOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.suppliedTrashItems')}</div>
          <div style={getDescStyle()}>{t('navigation.suppliedTrashItemsDesc')}</div>
        </div>
      </Link>,
    ];

    const toolsItems = [
      <Link
        to="/preset"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <PriceCheckOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.presetData')}</div>
          <div style={getDescStyle()}>{t('navigation.presetDataDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/items?t=droppers"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <Computer sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.droppers')}</div>
          <div style={getDescStyle()}>{t('navigation.droppersDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/analytics"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <AnalyticsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.analytics')}</div>
          <div style={getDescStyle()}>{t('navigation.analyticsDesc')}</div>
        </div>
      </Link>,
      <Link
        to="/sales"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <QueryStatsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.salesHistory')}</div>
          <div style={getDescStyle()}>{t('navigation.salesHistoryDesc')}</div>
        </div>
      </Link>,
    ];

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.pages')}{' '}
              <ShoppingCartCheckoutOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />{' '}
              <img style={{ width: '16px', verticalAlign: 'middle' }} src={chestImg} alt="chest" />
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                  }}
                >
                  <ShoppingCartCheckoutOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.sell')}
                </div>
                {sellItems}
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <img
                    src={chestImg}
                    alt="chest"
                    style={{ width: 18, height: 18, verticalAlign: 'middle' }}
                  />
                  {t('navigation.supply')}
                </div>
                {supplyItems}
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <ConstructionOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.tools')}
                </div>
                {toolsItems}
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderStaffDropdown = () => {
    if (!isLoggedIn || (!isStaff && !isD2RS && userData?.user?.can_prescreen !== 1)) return null;

    // Group items by permission
    const staffItems = [
      isStaff && (
        <Link
          to="/staff"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <AdminPanelSettingsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.nodeOverview')}</div>
            <div style={getDescStyle()}>{t('navigation.nodeOverviewDesc')}</div>
          </div>
        </Link>
      ),
      isStaff && (
        <Link
          to="/staff/users"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <PersonOutline sx={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.userOverview')}</div>
            <div style={getDescStyle()}>{t('navigation.userOverviewDesc')}</div>
          </div>
        </Link>
      ),
      isStaff && (
        <Link
          to="/staff/discords"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <Icon className="fab fa-discord" style={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.discordOverview')}</div>
            <div style={getDescStyle()}>{t('navigation.discordOverviewDesc')}</div>
          </div>
        </Link>
      ),
      isStaff && (
        <Link
          to="/staff/dynamic-pickit"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <Inventory2Outlined sx={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.dynamicPickitPacks')}</div>
            <div style={getDescStyle()}>{t('navigation.dynamicPickitPacksDesc')}</div>
          </div>
        </Link>
      ),
    ].filter(Boolean);

    const d2rsItems = [
      isD2RS && (
        <Link
          to="/orders"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.orders')}</div>
            <div style={getDescStyle()}>{t('navigation.ordersDesc')}</div>
          </div>
        </Link>
      ),
      isD2RS && (
        <Link
          to="/offers"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <GavelOutlined sx={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.offers')}</div>
            <div style={getDescStyle()}>{t('navigation.offersDesc')}</div>
          </div>
        </Link>
      ),
      <Link
        to="/preset"
        className={classes.dropdownLinkSpecial}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 223, 0, 0.1)',
          },
        }}
      >
        <PriceCheckOutlined sx={{ fontSize: 20, color: secondaryColor }} />
        <div>
          <div style={{ fontWeight: 600 }}>{t('navigation.presetData')}</div>
          <div style={getDescStyle()}>{t('navigation.presetDataDesc')}</div>
        </div>
      </Link>,
    ].filter(Boolean);

    const prescreenItems = [
      userData?.user?.can_prescreen === 1 && (
        <Link
          to="/prescreen"
          className={classes.dropdownLinkSpecial}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 223, 0, 0.1)',
            },
          }}
        >
          <Search sx={{ fontSize: 20, color: secondaryColor }} />
          <div>
            <div style={{ fontWeight: 600 }}>{t('navigation.prescreening')}</div>
            <div style={getDescStyle()}>{t('navigation.prescreeningDesc')}</div>
          </div>
        </Link>
      ),
    ].filter(Boolean);

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <>
              {t('navigation.staff')}{' '}
              <SupportAgentIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                  }}
                >
                  <AdminPanelSettingsOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.staff')}
                </div>
                {staffItems}
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <ShoppingCartCheckoutOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.d2rs')}
                </div>
                {d2rsItems}
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <Search sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.prescreen')}
                </div>
                {prescreenItems}
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderProfileDropdown = () => {
    if (!isLoggedIn) {
      return (
        <>
          <ListItem className={classes.listItem}>
            <Link
              to="/login"
              className={classes.navLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 223, 0, 0.1)',
                },
              }}
            >
              <LoginOutlined sx={{ fontSize: 16, color: secondaryColor }} />
              {t('navigation.login')}
            </Link>
          </ListItem>
          <ListItem className={classes.listItem}>
            <Link
              to="/register"
              className={classes.navLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 223, 0, 0.1)',
                },
              }}
            >
              <PersonAddOutlined sx={{ fontSize: 16, color: secondaryColor }} />
              {t('navigation.register')}
            </Link>
          </ListItem>
          <ListItem className={classes.listItem}>
            <a
              href={`https://discord.com/api/oauth2/authorize?client_id=${config.discord_client_id}&redirect_uri=${encodeURIComponent(
                `${window.location.origin}/auth/discord/callback`
              )}&response_type=code&scope=identify%20email`}
              className={classes.navLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease-in-out',
                color: '#7289da',
                '&:hover': {
                  backgroundColor: 'rgba(114, 137, 218, 0.1)',
                },
              }}
            >
              <Icon className="fab fa-discord" style={{ fontSize: 16, color: '#7289da' }} />
              {t('navigation.loginWithDiscord')}
            </a>
          </ListItem>
        </>
      );
    }

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {userData?.profile_image ? (
                <img
                  src={userData.profile_image}
                  alt="profile"
                  style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                />
              ) : (
                <PersonOutline sx={{ fontSize: 16 }} />
              )}
              {userData?.navigation_text || t('navigation.profile')}
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle()}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                  }}
                >
                  <PersonOutline sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.account')}
                </div>
                <Link
                  to="/profile"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  {userData?.profile_image ? (
                    <img
                      src={userData.profile_image}
                      alt="profile"
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                      }}
                    />
                  ) : (
                    <PersonOutline sx={{ fontSize: 20, color: secondaryColor }} />
                  )}
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.profile')}</div>
                    <div style={getDescStyle()}>{t('navigation.profileDesc')}</div>
                  </div>
                </Link>
                {userData?.user?.discord_id === '' && (
                  <a
                    href={`https://discord.com/api/oauth2/authorize?client_id=${config.discord_client_id}&redirect_uri=${encodeURIComponent(
                      `${window.location.origin}/auth/discord/callback`
                    )}&response_type=code&scope=identify%20email`}
                    className={classes.dropdownLinkSpecial}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease-in-out',
                      color: '#7289da',
                      '&:hover': {
                        backgroundColor: 'rgba(114, 137, 218, 0.1)',
                      },
                    }}
                  >
                    <Icon className="fab fa-discord" style={{ fontSize: 20, color: '#7289da' }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#7289da' }}>
                        {t('navigation.syncDiscord')}
                      </div>
                      <div style={getDescStyle()}>{t('navigation.syncDiscordDesc')}</div>
                    </div>
                  </a>
                )}
                <Link
                  to="/settings"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <SettingsOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.settings')}</div>
                    <div style={getDescStyle()}>{t('navigation.settingsDesc')}</div>
                  </div>
                </Link>
              </div>
              {/* Vertical divider */}
              <div style={getDividerStyle()} />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                  }}
                >
                  <LoginOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.auth')}
                </div>
                <Link
                  to="/logout"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 0.1)',
                    },
                  }}
                >
                  <LogoutOutlined sx={{ fontSize: 20, color: 'red' }} />
                  <div>
                    <div style={{ fontWeight: 600, color: 'red', whiteSpace: 'nowrap' }}>
                      {t('navigation.logout')}
                    </div>
                    <div style={getDescStyle()}>{t('navigation.logoutDesc')}</div>
                  </div>
                </Link>
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  // Helper function to get icon component by name
  const getIconComponent = (iconName, props = {}) => {
    const iconProps = { fontSize: 20, color: secondaryColor, ...props };

    switch (iconName) {
      case 'StarBorderOutlined':
        return <StarBorderOutlined sx={iconProps} />;
      case 'ViewModuleOutlined':
        return <ViewModuleOutlined sx={iconProps} />;
      case 'AutoAwesomeOutlined':
        return <AutoAwesomeOutlined sx={iconProps} />;
      case 'ShieldOutlined':
        return <ShieldOutlined sx={iconProps} />;
      case 'BlurOnOutlined':
        return <BlurOnOutlined sx={iconProps} />;
      case 'FiberNewOutlined':
        return <FiberNewOutlined sx={iconProps} />;
      case 'HelpOutlineOutlined':
        return <HelpOutlineOutlined sx={iconProps} />;
      case 'SportsKabaddiOutlined':
        return <SportsKabaddiOutlined sx={iconProps} />;
      case 'SecurityOutlined':
        return <SecurityOutlined sx={iconProps} />;
      case 'AutoFixHighOutlined':
        return <AutoFixHighOutlined sx={iconProps} />;
      case 'DiamondOutlined':
        return <DiamondOutlined sx={iconProps} />;
      case 'TagOutlined':
        return <TagOutlined sx={iconProps} />;
      case 'GavelOutlined':
        return <GavelOutlined sx={iconProps} />;
      case 'PriceChangeOutlined':
        return <PriceChangeOutlined sx={iconProps} />;
      default:
        return <FilterAltOutlined sx={iconProps} />;
    }
  };

  // Render browse items dropdown
  const renderBrowseItemsDropdown = () => {
    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.browseItems')}{' '}
              <FilterAltOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle('1fr 1px 1fr 1px 1fr 1px 1fr')}>
              {/* Rarities column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <StarBorderOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.rarities')}
                </div>
                {filterPresets.rarities.map((preset) => (
                  <div
                    key={preset.id}
                    className={classes.dropdownLinkSpecial}
                    onClick={() => {
                      const isAlreadyOnShopPage =
                        window.location.pathname.toLowerCase() === '/shop';
                      if (isAlreadyOnShopPage) {
                        // Apply filters directly when already on shop page
                        applyFiltersDirectly(preset.filters);
                      } else {
                        // Navigate with filters when not on shop page
                        D2RSService.navigateWithFilters(preset.filters, navigate, 'Shop', dispatch);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 223, 0, 0.1)',
                      },
                    }}
                  >
                    {getIconComponent(preset.icon)}
                    <div>
                      <div style={{ fontWeight: 600 }}>{t(preset.label)}</div>
                      <div style={getDescStyle()}>{t(preset.description)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div style={getDividerStyle()} />

              {/* Special column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <BlurOnOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.specialItems')}
                </div>
                {filterPresets.special.map((preset) => (
                  <div
                    key={preset.id}
                    className={classes.dropdownLinkSpecial}
                    onClick={() => {
                      const isAlreadyOnShopPage =
                        window.location.pathname.toLowerCase() === '/shop';
                      if (isAlreadyOnShopPage) {
                        // Apply filters directly when already on shop page
                        applyFiltersDirectly(preset.filters);
                      } else {
                        // Navigate with filters when not on shop page
                        D2RSService.navigateWithFilters(preset.filters, navigate, 'Shop', dispatch);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 223, 0, 0.1)',
                      },
                    }}
                  >
                    {getIconComponent(preset.icon)}
                    <div>
                      <div style={{ fontWeight: 600 }}>{t(preset.label)}</div>
                      <div style={getDescStyle()}>{t(preset.description)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div style={getDividerStyle()} />

              {/* Item Types column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <InventoryOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.itemTypes')}
                </div>
                {filterPresets.itemTypes.map((preset) => (
                  <div
                    key={preset.id}
                    className={classes.dropdownLinkSpecial}
                    onClick={() => {
                      const isAlreadyOnShopPage =
                        window.location.pathname.toLowerCase() === '/shop';
                      if (isAlreadyOnShopPage) {
                        // Apply filters directly when already on shop page
                        applyFiltersDirectly(preset.filters);
                      } else {
                        // Navigate with filters when not on shop page
                        D2RSService.navigateWithFilters(preset.filters, navigate, 'Shop', dispatch);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 223, 0, 0.1)',
                      },
                    }}
                  >
                    {getIconComponent(preset.icon)}
                    <div>
                      <div style={{ fontWeight: 600 }}>{t(preset.label)}</div>
                      <div style={getDescStyle()}>{t(preset.description)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div style={getDividerStyle()} />

              {/* Popular column */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <TrendingUpOutlined sx={{ fontSize: 18, color: secondaryColor }} />
                  {t('navigation.popularSearches')}
                </div>
                {filterPresets.popular.map((preset) => (
                  <div
                    key={preset.id}
                    className={classes.dropdownLinkSpecial}
                    onClick={() => {
                      const isAlreadyOnShopPage =
                        window.location.pathname.toLowerCase() === '/shop';
                      if (isAlreadyOnShopPage) {
                        // Apply filters directly when already on shop page
                        applyFiltersDirectly(preset.filters);
                      } else {
                        // Navigate with filters when not on shop page
                        D2RSService.navigateWithFilters(preset.filters, navigate, 'Shop', dispatch);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 223, 0, 0.1)',
                      },
                    }}
                  >
                    {getIconComponent(preset.icon)}
                    <div>
                      <div style={{ fontWeight: 600 }}>{t(preset.label)}</div>
                      <div style={getDescStyle()}>{t(preset.description)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>,
          ]}
          dropdownFooter={
            <div
              style={{
                padding: '12px 16px',
                marginTop: '8px',
                fontSize: '12px',
                opacity: 0.7,
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderTop: 'none',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
              }}
            >
              {t('navigation.browseItemsFooter')}
            </div>
          }
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  // Function to get overlapping skin images based on item name
  const getSkinImages = (itemName, typesData, categoriesData, raritiesData) => {
    // Hardcoded image mappings for specific categories that use custom images
    const categoryImageMappings = {
      'grand charm': ['eye.png', 'dna.png', 'monster.png'],
      'large charm': ['obelisk.png', 'paper.png', 'horn.png'],
      jewels: [
        'pink.png',
        'white.png',
        'orange.png',
        'peach.png',
        'red.png',
        'blue.png',
        'green.png',
      ],
      'small charm': ['football.png', 'paw.png', 'coin.png'],
      organs: ['baals_eye.png', 'diablos_horn.png', 'mephistos_brain.png'],
    };

    const itemNameLower = itemName.toLowerCase();

    // Handle categories with hardcoded images first
    const imageList = categoryImageMappings[itemNameLower];
    if (imageList) {
      const imagesToShow = imageList.slice(0, 3); // Show first 3 images for overlap

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            width: '48px',
            height: '24px',
          }}
        >
          {imagesToShow.map((imageName, index) => (
            <img
              key={imageName}
              src={`${config.backend_base_url}skin/${imageName}`}
              alt={itemName}
              onError={(e) => {
                e.target.src = d2rsIcon;
              }}
              style={{
                width: '24px',
                height: '24px',
                objectFit: 'contain',
                position: 'absolute',
                left: `${index * 12}px`,
                zIndex: 3 - index,
              }}
            />
          ))}
        </div>
      );
    }

    // For all other categories, find 3 matching types from API data
    const itemNameMapping = {
      weapons: 'weapon',
      helmets: 'helmet',
      armors: 'armor',
      shields: 'shield',
      circs: 'circlet',
      amulets: 'amulet',
      rings: 'ring',
      belts: 'belt',
      boots: 'boots',
      gloves: 'gloves',
      circlets: 'circlet',
      tokens: 'token',
      'horadric cube': 'cube',
      keys: 'key',
      essences: 'essence',
    };

    const mappedName = itemNameMapping[itemNameLower] || itemNameLower;

    // Special handling for weapons with specific type searches
    if (itemNameLower === 'weapons') {
      if (typesData && typesData.length > 0) {
        const weaponTypes = typesData
          .filter(
            (type) =>
              type.name.toLowerCase().includes('sword') ||
              type.name.toLowerCase().includes('hand to hand') ||
              type.name.toLowerCase().includes('orb') ||
              type.name.toLowerCase().includes('axe') ||
              type.name.toLowerCase().includes('mace') ||
              type.name.toLowerCase().includes('bow') ||
              type.name.toLowerCase().includes('spear') ||
              type.code?.toLowerCase().includes('swor') ||
              type.code?.toLowerCase().includes('h2h') ||
              type.code?.toLowerCase().includes('orb')
          )
          .slice(0, 3); // Take first 3 weapon types

        if (weaponTypes.length > 0) {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                width: '48px',
                height: '24px',
              }}
            >
              {weaponTypes.map((type, index) => (
                <img
                  key={type.id}
                  src={`${config.backend_base_url}skin/${type.skin_path}`}
                  alt={type.name}
                  onError={(e) => {
                    e.target.src = d2rsIcon;
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    objectFit: 'contain',
                    position: 'absolute',
                    left: `${index * 12}px`,
                    zIndex: 3 - index,
                  }}
                />
              ))}
            </div>
          );
        }
      }
    }

    // For all other categories, find matching types from API data
    if (typesData && typesData.length > 0) {
      const matchingTypes = typesData
        .filter(
          (type) =>
            type.name.toLowerCase().includes(mappedName) ||
            mappedName.includes(type.name.toLowerCase()) ||
            type.code?.toLowerCase().includes(mappedName.substring(0, 4)) ||
            (type.category &&
              type.category.name &&
              type.category.name.toLowerCase().includes(mappedName))
        )
        .slice(0, 3); // Take first 3 matching types

      if (matchingTypes.length > 0) {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              width: '48px',
              height: '24px',
            }}
          >
            {matchingTypes.map((type, index) => (
              <img
                key={type.id}
                src={`${config.backend_base_url}skin/${type.skin_path}`}
                alt={type.name}
                onError={(e) => {
                  e.target.src = d2rsIcon;
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  objectFit: 'contain',
                  position: 'absolute',
                  left: `${index * 12}px`,
                  zIndex: 3 - index,
                }}
              />
            ))}
          </div>
        );
      }
    }

    // Fallback: try to find from categories if types didn't work
    if (categoriesData && categoriesData.length > 0) {
      const matchingCategory = categoriesData.find(
        (cat) =>
          cat.name.toLowerCase().includes(mappedName) ||
          mappedName.includes(cat.name.toLowerCase()) ||
          cat.code?.toLowerCase() === mappedName
      );

      if (matchingCategory && matchingCategory.types && matchingCategory.types.length > 0) {
        const typesToShow = matchingCategory.types.slice(0, 3);
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              width: '48px',
              height: '24px',
            }}
          >
            {typesToShow.map((type, index) => (
              <img
                key={type.id}
                src={`${config.backend_base_url}skin/${type.skin_path}`}
                alt={type.name}
                onError={(e) => {
                  e.target.src = d2rsIcon;
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  objectFit: 'contain',
                  position: 'absolute',
                  left: `${index * 12}px`,
                  zIndex: 3 - index,
                }}
              />
            ))}
          </div>
        );
      }
    }

    // Final fallback - single image
    let fallbackImagePath = d2rsIcon;
    if (typesData && typesData.length > 0) {
      const type = typesData.find(
        (t) =>
          t.name.toLowerCase().includes(mappedName) ||
          mappedName.includes(t.name.toLowerCase()) ||
          t.code?.toLowerCase() === mappedName
      );
      if (type && type.skin_path) {
        fallbackImagePath = `${config.backend_base_url}skin/${type.skin_path}`;
      }
    }

    return (
      <img
        src={fallbackImagePath}
        alt={itemName}
        onError={(e) => {
          e.target.src = d2rsIcon;
        }}
        style={{
          width: '24px',
          height: '24px',
          objectFit: 'contain',
        }}
      />
    );
  };

  // Add new renderRaritiesDropdown function
  const renderRaritiesDropdown = () => {
    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.rarities')}/{t('navigation.categories')}{' '}
              <FilterAltOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div
              style={{
                display: isMobile ? 'flex' : 'grid',
                ...(isMobile
                  ? { flexDirection: 'column' }
                  : {
                      gridTemplateColumns: `repeat(${Object.keys(raritiesCategories).filter((key) => key !== 'rare').length}, 1fr)`,
                    }),
                gap: '16px',
                padding: '16px',
                width: '100%',
                ...(isMobile ? { maxWidth: '224px' } : { minWidth: '800px', maxWidth: '1200px' }),
                overflowX: 'auto',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              {/* Render each rarity category as its own column, excluding rare */}
              {Object.keys(raritiesCategories)
                .filter((rarityKey) => rarityKey !== 'rare') // Exclude rare category
                .map((rarityKey) => {
                  const rarityData = raritiesCategories[rarityKey];
                  return (
                    <div key={rarityKey} style={{ display: 'flex', flexDirection: 'column' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          fontWeight: 600,
                          marginBottom: '8px',
                          padding: '0 8px 8px 8px',
                          borderBottom: `1px solid ${secondaryColor}20`,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: rarityData.color,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                        {rarityData.name}
                      </div>
                      {/* Single column layout for items within this rarity */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          width: '100%',
                        }}
                      >
                        {rarityData.items.map((item) => (
                          <div
                            key={`${rarityKey}-${item}`}
                            className={classes.dropdownLinkSpecial}
                            onClick={() => {
                              // Create mapping from our rarity item names to actual category/type names for filtering
                              const itemNameMapping = {
                                helmets: 'helmet',
                                armors: 'armor',
                                shields: 'shield',
                                weapons: 'weapon',
                                circs: 'circlet',
                                amulets: 'amulet',
                                rings: 'ring',
                                belts: 'belt',
                                boots: 'boots',
                                gloves: 'gloves',
                                circlets: 'circlet',
                                'grand charm': 'grand charm',
                                'large charm': 'large charm',
                                'small charm': 'small charm',
                                tokens: 'token',
                                'horadric cube': 'horadric cube',
                                organs: 'organ',
                                keys: 'key',
                                essences: 'essence',
                              };

                              const mappedName =
                                itemNameMapping[item.toLowerCase()] || item.toLowerCase();

                              // Clear all existing filters first
                              dispatch(clearStatFilters());
                              dispatch(setStatFilterGroups([]));
                              dispatch(setRarity(''));
                              dispatch(setType(''));
                              dispatch(setItemType(''));
                              dispatch(setNewItems(''));
                              dispatch(setEthereal(''));
                              dispatch(setUnidentified(''));
                              dispatch(setLimitFilter(100));
                              dispatch(setViewType('all'));

                              // Create filter object with only the new filters
                              const filters = {
                                rarity: rarityKey === 'rare' ? 'rare' : rarityKey,
                                type: '',
                                itemType: '',
                              };

                              // Try to find matching category first
                              if (categoriesQuery.data && categoriesQuery.data.length > 0) {
                                const category = categoriesQuery.data.find(
                                  (cat) =>
                                    cat.name.toLowerCase().includes(mappedName) ||
                                    mappedName.includes(cat.name.toLowerCase()) ||
                                    cat.code?.toLowerCase() === mappedName
                                );
                                if (category) {
                                  filters.type = category.name; // Use name instead of ID
                                }
                              }

                              // If no category found, try to find specific item type
                              if (!filters.type && typesQuery.data && typesQuery.data.length > 0) {
                                const type = typesQuery.data.find(
                                  (t) =>
                                    t.name.toLowerCase().includes(mappedName) ||
                                    mappedName.includes(t.name.toLowerCase()) ||
                                    t.code?.toLowerCase() === mappedName
                                );
                                if (type) {
                                  filters.itemType = type.name; // Use name instead of ID
                                }
                              }

                              // Apply the new filters
                              dispatch(setRarity(filters.rarity));
                              if (filters.type) {
                                dispatch(setType(filters.type));
                              }
                              if (filters.itemType) {
                                dispatch(setItemType(filters.itemType));
                              }

                              // Use helper function to apply filters and generate search term
                              applyFiltersWithSearchTerm(filters);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              transition: 'all 0.2s ease-in-out',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 223, 0, 0.1)',
                              },
                            }}
                          >
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: rarityData.color,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                flexShrink: 0,
                              }}
                            />
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                minWidth: 0,
                              }}
                            >
                              {getSkinImages(
                                item,
                                typesQuery.data,
                                categoriesQuery.data,
                                raritiesQuery.data
                              )}
                              <span
                                style={{
                                  textTransform: 'capitalize',
                                  fontSize: '12px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {item}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  // Add new renderRareItemsDropdown function
  const renderRareItemsDropdown = () => {
    // Get only the rare category from rarities
    const rareCategory = raritiesCategories.rare;

    // Define the specific order for the 3x3 grid layout
    const itemOrder = [
      'amulets',
      'circlets',
      'rings',
      'weapons',
      'armors',
      'shields',
      'gloves',
      'belts',
      'boots',
    ];

    // Filter and order the items according to the specified layout
    const orderedItems = itemOrder
      .filter((item) =>
        rareCategory.items.some(
          (rareItem) =>
            rareItem.toLowerCase().includes(item.slice(0, -1)) || // Remove 's' and check
            rareItem.toLowerCase() === item ||
            (item === 'circlets' && rareItem.toLowerCase() === 'circs')
        )
      )
      .map((item) => {
        // Find the actual item name from the rareCategory.items
        return (
          rareCategory.items.find(
            (rareItem) =>
              rareItem.toLowerCase().includes(item.slice(0, -1)) ||
              rareItem.toLowerCase() === item ||
              (item === 'circlets' && rareItem.toLowerCase() === 'circs')
          ) || item
        );
      });

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.rareItems')}{' '}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  position: 'relative',
                  width: '20px',
                  height: '16px',
                  verticalAlign: 'middle',
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: rareCategory.color,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    position: 'absolute',
                    left: '0px',
                    zIndex: 2,
                  }}
                />
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#FF8C00',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    position: 'absolute',
                    left: '8px',
                    zIndex: 1,
                  }}
                />
              </div>
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '16px',
                width: '100%',
                minWidth: '500px',
                maxWidth: '700px',
                overflowX: 'auto',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  padding: '0 16px 8px 16px',
                  borderBottom: `1px solid ${secondaryColor}20`,
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: rareCategory.color,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                />
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#FF8C00',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                />
                {rareCategory.name}
              </div>

              {/* 3x3 Grid Layout */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '8px',
                  width: '100%',
                }}
              >
                {orderedItems.map((item) => (
                  <div
                    key={`rare-${item}`}
                    className={classes.dropdownLinkSpecial}
                    onClick={() => {
                      const itemNameMapping = {
                        helmets: 'helmet',
                        armors: 'armor',
                        shields: 'shield',
                        weapons: 'weapon',
                        circs: 'circlet',
                        amulets: 'amulet',
                        rings: 'ring',
                        belts: 'belt',
                        boots: 'boots',
                        gloves: 'gloves',
                        circlets: 'circlet',
                        'grand charm': 'grand charm',
                        'large charm': 'large charm',
                        'small charm': 'small charm',
                        tokens: 'token',
                        'horadric cube': 'horadric cube',
                        organs: 'organ',
                        keys: 'key',
                        essences: 'essence',
                      };

                      const mappedName = itemNameMapping[item.toLowerCase()] || item.toLowerCase();

                      // Clear all existing filters first
                      dispatch(clearStatFilters());
                      dispatch(setStatFilterGroups([]));
                      dispatch(setRarity(''));
                      dispatch(setType(''));
                      dispatch(setItemType(''));
                      dispatch(setNewItems(''));
                      dispatch(setEthereal(''));
                      dispatch(setUnidentified(''));
                      dispatch(setLimitFilter(100));
                      dispatch(setViewType('all'));

                      // Create filter object with rare rarity
                      const filters = {
                        rarity: 'rare',
                        type: '',
                        itemType: '',
                      };

                      // Try to find matching category first
                      if (categoriesQuery.data && categoriesQuery.data.length > 0) {
                        const category = categoriesQuery.data.find(
                          (cat) =>
                            cat.name.toLowerCase().includes(mappedName) ||
                            mappedName.includes(cat.name.toLowerCase()) ||
                            cat.code?.toLowerCase() === mappedName
                        );
                        if (category) {
                          filters.type = category.name; // Use name instead of ID
                        }
                      }

                      // If no category found, try to find specific item type
                      if (!filters.type && typesQuery.data && typesQuery.data.length > 0) {
                        const type = typesQuery.data.find(
                          (t) =>
                            t.name.toLowerCase().includes(mappedName) ||
                            mappedName.includes(t.name.toLowerCase()) ||
                            t.code?.toLowerCase() === mappedName
                        );
                        if (type) {
                          filters.itemType = type.name; // Use name instead of ID
                        }
                      }

                      // Apply the new filters
                      dispatch(setRarity(filters.rarity));
                      if (filters.type) {
                        dispatch(setType(filters.type));
                      }
                      if (filters.itemType) {
                        dispatch(setItemType(filters.itemType));
                      }

                      // Use helper function to apply filters and generate search term
                      applyFiltersWithSearchTerm(filters);
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      textAlign: 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 223, 0, 0.1)',
                      },
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: rareCategory.color,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      />
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#FF8C00',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      />
                    </div>
                    {getSkinImages(item, typesQuery.data, categoriesQuery.data, raritiesQuery.data)}
                    <span
                      style={{ textTransform: 'capitalize', fontSize: '12px', fontWeight: 600 }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };
  // Add new renderRuneDropdown function after renderRareItemsDropdown function
  const renderRuneDropdown = () => {
    const sessionToken = localStorage.getItem('session_token');

    // Query for the 4 featured runes with prices (only when logged in)
    const { data: featuredRunes, isLoading: loadingFeaturedRunes } = useQuery({
      queryKey: ['featured-runes', currentContext],
      queryFn: async () => {
        if (!sessionToken) return [];

        try {
          const runeNames = ['jah rune', 'ber rune', 'sur rune', 'lo rune'];
          const runePromises = runeNames.map(async (runeName) => {
            const response = await axios.post(
              `${config.backend_base_url}ml/pickit/dynamic/psearch`,
              { search_term: `.${runeName}` },
              { headers: { 'session-token': sessionToken } }
            );
            return response.data
              ? response.data.slice(0, 1).map((rune) => ({
                  ...rune,
                  name: rune.title || rune.name,
                  fiat_price:
                    rune.fg_price !== '???'
                      ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(
                          2
                        )
                      : '???',
                  context: rune.ladder
                    ? rune.hardcore
                      ? 'hcl'
                      : 'scl'
                    : rune.hardcore
                      ? 'hcnl'
                      : 'scnl',
                }))[0]
              : null;
          });

          const results = await Promise.all(runePromises);
          return results.filter(Boolean);
        } catch (error) {
          console.error('Error fetching featured runes:', error);
          return [];
        }
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!sessionToken, // Only run query when logged in
    });

    // Query for all runes (only when logged in)
    const { data: allRunes, isLoading: loadingAllRunes } = useQuery({
      queryKey: ['all-runes', currentContext],
      queryFn: async () => {
        if (!sessionToken) return [];

        try {
          const response = await axios.post(
            `${config.backend_base_url}ml/pickit/dynamic/psearch`,
            { search_term: ',rune' },
            { headers: { 'session-token': sessionToken } }
          );

          if (response.data && Array.isArray(response.data)) {
            // Filter out the featured runes and sort alphabetically
            const featuredRuneNames = ['jah rune', 'ber rune', 'sur rune', 'lo rune'];
            return response.data
              .filter((rune) => {
                const runeName = (rune.title || rune.name || '').toLowerCase();
                return !featuredRuneNames.some((featured) =>
                  runeName.includes(featured.replace(' rune', ''))
                );
              })
              .map((rune) => ({
                ...rune,
                name: rune.title || rune.name,
              }))
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          }
          return [];
        } catch (error) {
          console.error('Error fetching all runes:', error);
          return [];
        }
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!sessionToken, // Only run query when logged in
    });

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.runes')}{' '}
              <img
                src={`${config.backend_base_url}skin/ber_rune.png`}
                alt="runes"
                style={{ width: '16px', height: '16px', verticalAlign: 'middle' }}
                onError={(e) => {
                  e.target.src = d2rsIcon;
                }}
              />
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '16px',
                width: '100%',
                minWidth: '600px',
                maxWidth: '900px',
                overflowX: 'auto',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              {/* Filter for all runes */}
              <div>
                <div
                  className={classes.dropdownLinkSpecial}
                  onClick={() => {
                    const filters = { rarity: 'rune' };

                    // Clear existing filters first
                    dispatch(clearStatFilters());
                    dispatch(setStatFilterGroups([]));
                    dispatch(setRarity(''));
                    dispatch(setType(''));
                    dispatch(setItemType(''));
                    dispatch(setNewItems(''));
                    dispatch(setEthereal(''));
                    dispatch(setUnidentified(''));
                    dispatch(setLimitFilter(100));
                    dispatch(setViewType('all'));

                    // Apply rune filter
                    dispatch(setRarity('rune'));

                    // Use helper function to apply filters and generate search term
                    applyFiltersWithSearchTerm(filters);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    border: `2px solid ${secondaryColor}40`,
                    borderRadius: '8px',
                    '&:hover': {
                      border: `3px solid ${secondaryColor}`,
                    },
                  }}
                >
                  <img
                    src={`${config.backend_base_url}skin/ber_rune.png`}
                    alt="runes"
                    style={{ width: '20px', height: '20px' }}
                    onError={(e) => {
                      e.target.src = d2rsIcon;
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('navigation.viewAllRunes')}</div>
                    <div style={getDescStyle()}>{t('navigation.viewAllRunesDesc')}</div>
                  </div>
                </div>
              </div>

              {/* Featured runes with prices */}
              {sessionToken ? (
                <div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '8px',
                    }}
                  >
                    {loadingFeaturedRunes ? (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                        Loading...
                      </div>
                    ) : (
                      featuredRunes?.map((rune) => (
                        <div
                          key={rune.id}
                          className={classes.dropdownLinkSpecial}
                          onClick={() => {
                            window.open(`/product/${rune.id}`, '_blank');
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '8px',
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 223, 0, 0.2)',
                            position: 'relative',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 223, 0, 0.1)',
                            },
                          }}
                        >
                          {/* Price badge */}
                          {rune.fiat_price !== '???' && !isNaN(parseFloat(rune.fiat_price)) && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                backgroundColor: secondaryColor,
                                color: 'black',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                zIndex: 1,
                              }}
                            >
                              ${parseFloat(rune.fiat_price).toFixed(0)}
                            </div>
                          )}
                          {/* Context indicators */}
                          {rune.context && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '4px',
                                left: '4px',
                                display: 'flex',
                                gap: '2px',
                                zIndex: 1,
                              }}
                            >
                              {(rune.context === 'scl' || rune.context === 'scnl') && (
                                <img style={{ width: '12px' }} src={hardcoreOff} alt="softcore" />
                              )}
                              {(rune.context === 'hcl' || rune.context === 'hcnl') && (
                                <img style={{ width: '12px' }} src={hardcoreOn} alt="hardcore" />
                              )}
                              {(rune.context === 'scl' || rune.context === 'hcl') && (
                                <img style={{ width: '12px' }} src={ladderOn} alt="ladder" />
                              )}
                              {(rune.context === 'scnl' || rune.context === 'hcnl') && (
                                <img style={{ width: '12px' }} src={ladderOff} alt="non-ladder" />
                              )}
                            </div>
                          )}
                          <img
                            src={`${config.backend_base_url}skin/${rune.skin_image_path}`}
                            alt={rune.name}
                            style={{
                              width: '32px',
                              height: '32px',
                              objectFit: 'contain',
                            }}
                            onError={(e) => {
                              e.target.src = d2rsIcon;
                            }}
                          />
                          <span style={{ fontSize: '10px', fontWeight: 600, textAlign: 'center' }}>
                            {rune.name}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '16px',
                    border: '1px solid rgba(255, 223, 0, 0.2)',
                    borderRadius: '8px',
                    opacity: 0.7,
                  }}
                >
                  <LoginOutlined sx={{ fontSize: 24, color: secondaryColor }} />
                  <div style={{ textAlign: 'center', fontSize: '12px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {t('navigation.loginRequired')}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {t('navigation.loginToViewPrices')}
                    </div>
                  </div>
                </div>
              )}

              {/* All other runes */}
              {sessionToken ? (
                <div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: '4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                  >
                    {loadingAllRunes ? (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                        Loading...
                      </div>
                    ) : (
                      allRunes?.map((rune) => (
                        <div
                          key={rune.id}
                          className={classes.dropdownLinkSpecial}
                          onClick={() => {
                            window.open(`/product/${rune.id}`, '_blank');
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 223, 0, 0.1)',
                            },
                          }}
                        >
                          <img
                            src={`${config.backend_base_url}skin/${rune.skin_image_path}`}
                            alt={rune.name}
                            style={{
                              width: '16px',
                              height: '16px',
                              objectFit: 'contain',
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              e.target.src = d2rsIcon;
                            }}
                          />
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {rune.name}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  // Character class data
  const characterClasses = [
    {
      name: 'Paladin',
      image: paladinClass,
      className: 'paladin',
      filters: [], // No specific filters for paladin
    },
    {
      name: 'Sorceress',
      image: sorceressClass,
      className: 'sorceress',
      filters: [], // No specific filters for sorceress
    },
    {
      name: 'Amazon',
      image: amazonClass,
      className: 'amazon',
      filters: [], // No specific filters for amazon
    },
    {
      name: 'Druid',
      image: druidClass,
      className: 'druid',
      filters: [], // No specific filters for druid
    },
    {
      name: 'Barbarian',
      image: barbarianClass,
      className: 'barbarian',
      filters: [], // No specific filters for barbarian
    },
    {
      name: 'Assassin',
      image: assassinClass,
      className: 'assassin',
      filters: [
        {
          id: 'assassin-rare-circlet',
          label: 'Rare Circlets with Assassin Skills',
          filters: {
            rarity: 'rare',
            type: '', // We'll find circlet category
            itemType: '', // We'll find circlet type if needed
            statFilterGroups: [
              {
                id: 'assassin-skills',
                filters: [
                  {
                    id: 'assassin-skill-stat',
                    name: 'Assassin Skill Levels',
                    min: 1,
                    max: 999,
                  },
                ],
              },
            ],
          },
        },
        {
          id: 'assassin-rare-h2h',
          label: 'Rare Hand-to-Hand Weapons',
          filters: {
            rarity: 'rare',
            type: '', // We'll find hand-to-hand category
            itemType: '',
            statFilterGroups: [],
          },
        },
        {
          id: 'assassin-magic-h2h',
          label: 'Magic Hand-to-Hand Weapons',
          filters: {
            rarity: 'magic',
            type: '', // We'll find hand-to-hand category
            itemType: '',
            statFilterGroups: [],
          },
        },
      ],
    },
    {
      name: 'Necromancer',
      image: necromancerClass,
      className: 'necromancer',
      filters: [
        {
          id: 'necro-rare-circlet',
          label: 'Rare Circlets with Necromancer Skills',
          filters: {
            rarity: 'rare',
            type: '', // We'll find circlet category
            itemType: '',
            statFilterGroups: [
              {
                id: 'necro-skills',
                filters: [
                  {
                    id: 'necro-skill-stat',
                    name: 'Necromancer Skill Levels',
                    min: 1,
                    max: 999,
                  },
                ],
              },
            ],
          },
        },
        {
          id: 'necro-magic-wand',
          label: 'Magic Wands',
          filters: {
            rarity: 'magic',
            type: '', // We'll find wand category
            itemType: '',
            statFilterGroups: [],
          },
        },
        {
          id: 'necro-pnb-gc',
          label: 'Grand Charms with Poison and Bone Skills',
          filters: {
            rarity: '',
            type: '', // We'll find grand charm category
            itemType: '',
            statFilterGroups: [
              {
                id: 'pnb-skills',
                filters: [
                  {
                    id: 'pnb-skill-stat',
                    name: 'Poison and Bone Skills',
                    min: 1,
                    max: 999,
                  },
                ],
              },
            ],
          },
        },
      ],
    },
  ];

  // Helper function to get category/type IDs for character filters
  const getFilterIds = (filterName, typesData, categoriesData) => {
    const lowerFilterName = filterName.toLowerCase();
    console.log(
      'getFilterIds called with:',
      filterName,
      'typesData:',
      typesData?.length,
      'categoriesData:',
      categoriesData?.length
    );

    // More specific mapping for common item types
    const itemTypeMapping = {
      circlet: ['circlet'],
      wand: ['wand'],
      'grand charm': ['grand charm', 'charm'],
      'hand-to-hand': ['hand to hand', 'claw', 'assassin claw'],
    };

    // Check if any of the mapped terms are in the filter name
    for (const [key, terms] of Object.entries(itemTypeMapping)) {
      if (lowerFilterName.includes(key)) {
        // Try to find in categories first
        if (categoriesData) {
          const category = categoriesData.find((cat) =>
            terms.some((term) => cat.name.toLowerCase().includes(term))
          );
          if (category) {
            console.log('Found category:', category.name, 'for key:', key);
            return { type: category.name, itemType: '' };
          }
        }

        // Try to find in types if not found in categories
        if (typesData) {
          const type = typesData.find((t) =>
            terms.some((term) => t.name.toLowerCase().includes(term))
          );
          if (type) {
            console.log('Found type:', type.name, 'for key:', key);
            return { type: '', itemType: type.name };
          }
        }
      }
    }

    // Fallback to original logic for broader matching
    if (categoriesData) {
      const category = categoriesData.find(
        (cat) =>
          cat.name.toLowerCase().includes(lowerFilterName) ||
          lowerFilterName.includes(cat.name.toLowerCase())
      );

      if (category) {
        console.log('Found category via fallback:', category.name);
        return { type: category.name, itemType: '' };
      }
    }

    if (typesData) {
      const type = typesData.find(
        (t) =>
          t.name.toLowerCase().includes(lowerFilterName) ||
          lowerFilterName.includes(t.name.toLowerCase())
      );

      if (type) {
        console.log('Found type via fallback:', type.name);
        return { type: '', itemType: type.name };
      }
    }

    console.log('No match found for:', filterName);
    return { type: '', itemType: '' };
  };

  // Helper function to get stat IDs for character skills
  const getStatIds = (skillName, statsData) => {
    if (!statsData || !Array.isArray(statsData)) {
      console.log('getStatIds: No stats data available');
      return '';
    }

    const lowerSkillName = skillName.toLowerCase();
    console.log('getStatIds called with:', skillName, 'statsData length:', statsData.length);

    // More specific mappings for character skills
    const skillMapping = {
      'necromancer skill': ['necromancer skill', 'necro skill', 'necromancer'],
      'assassin skill': ['assassin skill', 'assassin'],
      'paladin skill': ['paladin skill', 'paladin'],
      'sorceress skill': ['sorceress skill', 'sorceress', 'sorc skill'],
      'amazon skill': ['amazon skill', 'amazon', 'ama skill'],
      'druid skill': ['druid skill', 'druid'],
      'barbarian skill': ['barbarian skill', 'barbarian', 'barb skill'],
      'poison and bone': ['poison and bone', 'poison & bone', 'pnb'],
    };

    // First try specific mappings
    for (const [key, variations] of Object.entries(skillMapping)) {
      if (variations.some((variation) => lowerSkillName.includes(variation))) {
        const stat = statsData.find((s) => {
          const statName = s.name.toLowerCase();
          return variations.some((variation) => statName.includes(variation));
        });
        if (stat) {
          console.log('Found stat via mapping:', stat.name, 'for skill:', skillName);
          return stat.id;
        }
      }
    }

    // Fallback to broader search
    const stat = statsData.find((s) => {
      const statName = s.name.toLowerCase();
      return statName.includes(lowerSkillName) || lowerSkillName.includes(statName);
    });

    if (stat) {
      console.log('Found stat via fallback:', stat.name, 'for skill:', skillName);
      return stat.id;
    }

    console.log('No stat found for skill:', skillName);
    return '';
  };

  // Apply character filter combination
  const applyCharacterFilter = (filterCombination) => {
    console.log('applyCharacterFilter called with:', filterCombination);

    // Clear existing filters first
    dispatch(clearStatFilters());
    dispatch(setStatFilterGroups([]));
    dispatch(setRarity(''));
    dispatch(setType(''));
    dispatch(setItemType(''));
    dispatch(setNewItems(''));
    dispatch(setEthereal(''));
    dispatch(setUnidentified(''));
    dispatch(setLimitFilter(100));
    dispatch(setViewType('all'));

    const filters = filterCombination.filters;

    // Create a proper FilterState object for D2RSService
    const filterState = {
      rarity: filters.rarity || '',
      type: '',
      itemType: '',
      newItems: '',
      ethereal: '',
      unidentified: '',
      limitFilter: 100,
      viewType: 'all',
      priceFilter: 'all',
      excludeStatFilters: [],
      statFilterGroups: [],
      isFilterBubbleOpen: false,
      savedSearches: [],
      savedSearchSent: false,
      searchTerm: '',
      shouldApplySearch: false,
    };

    // Set type/itemType based on the filter
    const typeIds = getFilterIds(filterCombination.label, typesQuery.data, categoriesQuery.data);
    console.log('Type IDs resolved:', typeIds);
    if (typeIds.type) {
      filterState.type = typeIds.type;
    }
    if (typeIds.itemType) {
      filterState.itemType = typeIds.itemType;
    }

    // Set stat filter groups if present
    if (filters.statFilterGroups && filters.statFilterGroups.length > 0) {
      filterState.statFilterGroups = filters.statFilterGroups.map((group) => ({
        ...group,
        filters: group.filters.map((filter) => {
          const statId = getStatIds(filter.name, statsQuery.data);
          console.log('Stat ID resolved for', filter.name, ':', statId);
          return {
            ...filter,
            id: statId || filter.id,
          };
        }),
      }));
    }

    // Check if we're on shop page
    const isAlreadyOnShopPage = window.location.pathname.toLowerCase() === '/shop';

    if (isAlreadyOnShopPage) {
      // Apply the individual filters to Redux state
      if (filterState.rarity) {
        dispatch(setRarity(filterState.rarity));
      }
      if (filterState.type) {
        dispatch(setType(filterState.type));
      }
      if (filterState.itemType) {
        dispatch(setItemType(filterState.itemType));
      }
      if (filterState.statFilterGroups.length > 0) {
        dispatch(setStatFilterGroups(filterState.statFilterGroups));
      }

      // Generate search term from filters and set it
      const searchTerm = D2RSService.convertFiltersToSearchString(filterState);
      if (searchTerm) {
        dispatch(setSearchTerm(searchTerm));
      }

      // Apply the search
      dispatch(applyFilterSearch());
    } else {
      // Generate search term from filters and set it BEFORE navigation
      const searchTerm = D2RSService.convertFiltersToSearchString(filterState);

      // Set the search term in Redux state
      if (searchTerm) {
        dispatch(setSearchTerm(searchTerm));
      }
      dispatch(applyFilterSearch());

      D2RSService.navigateWithFilters(filterState, navigate, 'Shop', dispatch);
    }
  };

  // Render character classes dropdown
  const renderCharacterClassesDropdown = () => {
    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <span>
              {t('navigation.characters')}{' '}
              <img
                src={paladinClass}
                alt="paladin"
                style={{ width: '16px', height: '16px', verticalAlign: 'middle' }}
              />
            </span>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '16px',
                width: '100%',
                minWidth: '800px',
                maxWidth: '1200px',
                overflowX: 'auto',
                border: '1px solid rgba(255, 223, 0, 0.2)',
                borderRadius: '8px',
              }}
            >
              {/* View all classes link */}
              <Link
                to="/classes"
                className={classes.dropdownLinkSpecial}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 223, 0, 0.3)',
                  fontSize: '12px',
                  marginBottom: '16px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 223, 0, 0.1)',
                  },
                }}
              >
                <Diversity1Outlined sx={{ fontSize: 16, color: secondaryColor }} />
                {t('navigation.viewAllClasses')}
              </Link>

              {/* Character grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '16px',
                }}
              >
                {characterClasses.map((character) => (
                  <div
                    key={character.className}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      padding: '16px',
                      border: '1px solid rgba(255, 223, 0, 0.2)',
                      borderRadius: '8px',
                    }}
                  >
                    {/* Character header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                      }}
                    >
                      <img
                        src={character.image}
                        alt={character.name}
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'cover',
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '16px' }}>{character.name}</div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Link
                        to={`/class/${character.className}`}
                        className={classes.dropdownLinkSpecial}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          border: '1px solid rgba(255, 223, 0, 0.3)',
                          fontSize: '12px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <InfoOutlined sx={{ fontSize: 16, color: secondaryColor }} />
                        {t('class.details.label')}
                      </Link>
                      <Link
                        to={`/class/${character.className}?tab=guides`}
                        className={classes.dropdownLinkSpecial}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          border: '1px solid rgba(255, 223, 0, 0.3)',
                          fontSize: '12px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <MenuBookOutlined sx={{ fontSize: 16, color: secondaryColor }} />
                        {t('class.guides.label')}
                      </Link>
                      <Link
                        to={`/class/${character.className}?tab=packs`}
                        className={classes.dropdownLinkSpecial}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          border: '1px solid rgba(255, 223, 0, 0.3)',
                          fontSize: '12px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 223, 0, 0.1)',
                          },
                        }}
                      >
                        <InventoryOutlined sx={{ fontSize: 16, color: secondaryColor }} />
                        {t('class.packs.label')}
                      </Link>
                    </div>

                    {/* Character-specific filters */}
                    {character.filters.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: secondaryColor,
                            textAlign: 'center',
                            borderBottom: `1px solid ${secondaryColor}20`,
                            paddingBottom: '4px',
                          }}
                        >
                          {t('navigation.quickFilters')}
                        </div>
                        {character.filters.map((filter) => (
                          <div
                            key={filter.id}
                            className={classes.dropdownLinkSpecial}
                            onClick={() => applyCharacterFilter(filter)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              transition: 'all 0.2s ease-in-out',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              border: '1px solid rgba(255, 223, 0, 0.3)',
                              fontSize: '12px',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 223, 0, 0.1)',
                              },
                            }}
                          >
                            {/* Rarity icon */}
                            {filter.filters.rarity === 'rare' && (
                              <div
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#FFFF00',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {filter.filters.rarity === 'magic' && (
                              <div
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#6699FF',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {!filter.filters.rarity && (
                              <FilterAltOutlined sx={{ fontSize: 12, color: secondaryColor }} />
                            )}

                            <span style={{ fontSize: '11px', lineHeight: '1.2' }}>
                              {filter.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  const renderMyOrdersDropdown = () => {
    if (!isLoggedIn) return null;

    return (
      <ListItem className={classes.listItem}>
        <CustomDropdown
          noLiPadding
          buttonText={
            <>
              {t('navigation.orders')}{' '}
              <ListAltOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </>
          }
          buttonProps={{
            className: classes.navLink,
            color: 'transparent',
          }}
          dropdownList={[
            <div style={getGridStyle('1fr')}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    padding: '0 16px 8px 16px',
                    borderBottom: `1px solid ${secondaryColor}20`,
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <ListAltOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  {t('navigation.orders')}
                </div>
                <Link
                  to="/my-orders?status=request_delivery"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <SwapHorizOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('orders.request_delivery')}</div>
                    <div style={getDescStyle()}>{t('orders.request_delivery')}</div>
                  </div>
                </Link>
                <Link
                  to="/my-orders?status=in_progress"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <LocalShippingOutlined sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('orders.in_progress')}</div>
                    <div style={getDescStyle()}>{t('orders.in_progress')}</div>
                  </div>
                </Link>
                <Link
                  to="/my-orders?status=completed"
                  className={classes.dropdownLinkSpecial}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 223, 0, 0.1)',
                    },
                  }}
                >
                  <CheckCircleOutline sx={{ fontSize: 20, color: secondaryColor }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('orders.completed')}</div>
                    <div style={getDescStyle()}>{t('orders.completed')}</div>
                  </div>
                </Link>
              </div>
            </div>,
          ]}
          menuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${secondaryColor}40`,
                borderRadius: '8px',
                boxShadow: `0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 10px -5px ${secondaryColor}40`,
                overflow: 'hidden',
                maxWidth: 'calc(100vw - 32px)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
                '& .MuiMenuItem-root': {
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
        />
      </ListItem>
    );
  };

  return (
    <List className={classes.list}>
      {renderContextSelector()}
      {renderNodeSelector()}
      {renderPagesDropdown()}
      {/* {renderBrowseItemsDropdown()} */}
      {renderRuneDropdown()}
      {renderRareItemsDropdown()}
      {renderRaritiesDropdown()}
      {renderCharacterClassesDropdown()}
      {renderMyOrdersDropdown()}
      {/* {renderSearchDropdown()} */}
      {renderToolsDropdown()}
      {renderInfoDropdown()}
      {renderTourDropdown()}
      {renderNodePagesDropdown()}
      {renderStaffDropdown()}
      {renderProfileDropdown()}
    </List>
  );
}
